import React, { useState, useEffect } from 'react';
import { Play, Image as ImageIcon, Sparkles, Loader } from 'lucide-react';
import { API_BASE, getImageUrl } from '../config';

export default function Gallery({ t }) {
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    fetch(`${API_BASE}/gallery`)
      .then(res => res.json())
      .then(data => setGalleryItems(data))
      .catch(err => console.error("Error loading gallery:", err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-white via-slate-50 to-white">
        <div className="text-center">
          <Loader className="animate-spin h-16 w-16 text-brand-600 mx-auto mb-4" />
          <p className="text-slate-600 font-medium text-lg">Chargement de la galerie...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-slate-50 to-white">
      {/* En-tête élégant */}
      <div className="relative py-12 px-4 bg-gradient-to-br from-slate-900 via-brand-900 to-slate-800 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yIDItNCAzLTRzMyAyIDMgNHYyYzAgMi0yIDQtMyA0cy0zLTItMy00di0yem0wLTMwYzAtMiAyLTQgMy00czMgMiAzIDR2MmMwIDItMiA0LTMgNHMtMy0yLTMtNFY0ek0wIDM0YzAtMiAyLTQgMy00czMgMiAzIDR2MmMwIDItMiA0LTMgNHMtMy0yLTMtNHYtMnptMC0zMGMwLTIgMi00IDMtNHMzIDIgMyA0djJjMCAyLTIgNC0zIDRzLTMtMi0zLTRWNHoiLz48L2c+PC9nPjwvc3ZnPg==')] animate-pulse"></div>
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center justify-center p-1 mb-4 bg-white/10 backdrop-blur-xl rounded-full border border-white/30 px-4">
            <Sparkles className="h-3 w-3 mr-2 text-brand-300" />
            <span className="text-[9px] font-black uppercase tracking-[0.2em] text-brand-50">
              {t.reviews?.badge || 'Témoignages Authentiques'}
            </span>
          </div>
          
          <h2 className="text-3xl md:text-4xl font-black text-white mb-4 font-serif italic tracking-tight">
            {t.gallery?.title || 'Notre Galerie'}
          </h2>
          <p className="text-xl text-slate-200 font-medium opacity-90 max-w-2xl mx-auto">
            {t.gallery?.subtitle || 'Découvrez nos plus belles réalisations'}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-16">
        {galleryItems.length === 0 ? (
          <div className="text-center bg-gradient-to-br from-slate-50 to-brand-50 py-20 px-8 rounded-3xl border-2 border-dashed border-slate-300">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-white rounded-full shadow-lg mb-6">
              <ImageIcon className="h-12 w-12 text-slate-400" />
            </div>
            <p className="text-slate-600 text-xl font-bold">
              {t.gallery?.empty || 'Aucune photo disponible pour le moment'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleryItems.map((item, index) => (
              <div 
                key={item.id} 
                className="group relative overflow-hidden rounded-3xl shadow-lg aspect-square bg-slate-100 hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer"
                style={{ animationDelay: `${index * 50}ms` }}
                onClick={() => setSelectedItem(item)}
              >
                {item.type === 'video' ? (
                  <div className="w-full h-full relative">
                    <video
                      src={getImageUrl(item.url)}
                      className="w-full h-full object-cover"
                      preload="metadata"
                      poster={getImageUrl(item.url) + '#t=0.1'}
                    />
                    {/* Badge vidéo */}
                    <div className="absolute top-4 right-4 bg-black/70 backdrop-blur-sm text-white px-3 py-2 rounded-full flex items-center gap-2 font-bold text-xs uppercase tracking-wider">
                      <Play className="h-3 w-3 fill-current" />
                      Vidéo
                    </div>
                    {/* Overlay avec bouton play */}
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform shadow-2xl">
                        <Play className="h-10 w-10 text-brand-600 fill-current ml-1" />
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <img
                      src={getImageUrl(item.url)}
                      alt="Gallery"
                      className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                      loading="lazy"
                    />
                    {/* Overlay subtle au hover */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </>
                )}
                
                {/* Bordure animée au hover */}
                <div className="absolute inset-0 border-4 border-brand-600 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal lightbox pour afficher l'image/vidéo en grand */}
      {selectedItem && (
        <div 
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={() => setSelectedItem(null)}
        >
          <button
            onClick={() => setSelectedItem(null)}
            className="absolute top-6 right-6 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white font-black text-2xl transition-all hover:rotate-90"
          >
            ×
          </button>
          
          <div className="max-w-6xl max-h-[90vh] w-full" onClick={(e) => e.stopPropagation()}>
            {selectedItem.type === 'video' ? (
              <video
                src={getImageUrl(selectedItem.url)}
                className="w-full h-full rounded-3xl shadow-2xl"
                controls
                autoPlay
              />
            ) : (
              <img
                src={getImageUrl(selectedItem.url)}
                alt="Gallery full"
                className="w-full h-full object-contain rounded-3xl shadow-2xl"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}